import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, imageUrl } from "../../lib/api";

const BLANK = {
  name: "",
  location: "",
  city: "mumbai",
  listing_type: "New Project",
  config: "",
  price: "",
  status: "Under Construction",
  platforms: [],
  source_url: "",
  image: "",
  note: "",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0B0C0E] px-4 py-3 text-sm text-[#F8FAFC] outline-none transition-colors placeholder:text-[#64748B] focus:border-[#D4AF37]/60";

const Label = ({ children }) => (
  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8B93A5]">{children}</p>
);

const Pill = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
      active
        ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F3E5AB]"
        : "border-white/10 text-[#94A3B8] hover:border-white/30"
    }`}
  >
    {children}
  </button>
);

export default function ProjectForm({ project, onClose }) {
  const isEdit = !!project?.id;
  const [form, setForm] = useState(project?.id ? { ...project } : { ...BLANK });
  const [uploading, setUploading] = useState(false);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: (payload) =>
      isEdit
        ? api.put(`/api/projects/${project.id}`, payload)
        : api.post("/api/projects", payload),
    onSuccess: () => {
      toast.success(isEdit ? "Project updated" : "Project published to the website");
      qc.invalidateQueries({ queryKey: ["projects"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/admin/upload", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const togglePlatform = (k) =>
    setForm((f) => ({
      ...f,
      platforms: f.platforms.includes(k)
        ? f.platforms.filter((x) => x !== k)
        : [...f.platforms, k],
    }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(form);
      }}
      data-testid="admin-project-form"
      className="mt-8 rounded-3xl border border-[#D4AF37]/25 bg-[#13151B] p-6 sm:p-8"
    >
      <h2 className="font-serif text-2xl font-semibold text-[#F3E5AB]">
        {isEdit ? `Edit — ${project.name}` : "Add New Project"}
      </h2>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <Label>Project Name</Label>
          <input data-testid="admin-form-name" required value={form.name} onChange={set("name")} placeholder="e.g. Signature Isle" className={inputCls} />
        </div>
        <div>
          <Label>Location</Label>
          <input data-testid="admin-form-location" required value={form.location} onChange={set("location")} placeholder="e.g. Worli Sea Face, Mumbai" className={inputCls} />
        </div>
        <div>
          <Label>Price Range</Label>
          <input data-testid="admin-form-price" value={form.price} onChange={set("price")} placeholder="e.g. ₹2.4 – 4.8 Cr" className={inputCls} />
        </div>
        <div>
          <Label>Configuration</Label>
          <input data-testid="admin-form-config" value={form.config} onChange={set("config")} placeholder="e.g. 3 & 4 BHK" className={inputCls} />
        </div>
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <Label>City</Label>
          <div className="flex gap-2">
            <Pill testid="admin-form-city-mumbai" active={form.city === "mumbai"} onClick={() => setForm((f) => ({ ...f, city: "mumbai" }))}>Mumbai</Pill>
          </div>
        </div>
        <div>
          <Label>Listing Type</Label>
          <div className="flex flex-wrap gap-2">
            <Pill testid="admin-form-type-new-project" active={form.listing_type === "New Project"} onClick={() => setForm((f) => ({ ...f, listing_type: "New Project" }))}>New Project</Pill>
            <Pill testid="admin-form-type-resale" active={form.listing_type === "Resale"} onClick={() => setForm((f) => ({ ...f, listing_type: "Resale" }))}>Resale</Pill>
            <Pill testid="admin-form-featured" active={!!form.featured} onClick={() => setForm((f) => ({ ...f, featured: !f.featured }))}>Pin as Priority</Pill>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <Label>Status / Possession</Label>
        <input data-testid="admin-form-status" value={form.status} onChange={set("status")} placeholder="e.g. Under Construction · Possession 2028" className={inputCls} />
        <div className="mt-2 flex flex-wrap gap-2">
          {["Under Construction", "Ready to Move", "New Launch"].map((s) => (
            <button
              type="button"
              key={s}
              data-testid={`admin-form-status-suggest-${s.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setForm((f) => ({ ...f, status: s }))}
              className="rounded-full border border-[#E7E3D8] bg-white px-3 py-1 text-xs font-medium text-navy/60 transition-colors hover:border-gold-deep hover:text-gold-deep"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <Label>Listed On</Label>
        <div className="flex gap-2">
          <Pill testid="admin-form-platform-housing" active={form.platforms.includes("housing")} onClick={() => togglePlatform("housing")}>Housing.com</Pill>
          <Pill testid="admin-form-platform-acres" active={form.platforms.includes("acres")} onClick={() => togglePlatform("acres")}>99acres</Pill>
        </div>
      </div>

      <div className="mt-6">
        <Label>Source Listing Link — shown as &quot;View on portal&quot;</Label>
        <input data-testid="admin-form-source-url" value={form.source_url} onChange={set("source_url")} placeholder="Paste the Housing.com / 99acres listing URL (optional)" className={inputCls} />
      </div>

      <div className="mt-6">
        <Label>Cover Photo</Label>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          {form.image && (
            <img src={imageUrl(form.image)} alt="Cover preview" data-testid="admin-form-image-preview" className="h-24 w-36 rounded-xl border border-white/10 object-cover" />
          )}
          <div className="flex-1 space-y-3">
            <label
              data-testid="admin-form-upload-button"
              className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-[#D4AF37]/40 px-4 py-3 text-sm font-medium text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Upload photo from device"}
              <input data-testid="admin-form-upload-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={upload} />
            </label>
            <input data-testid="admin-form-image-url" value={form.image} onChange={set("image")} placeholder="…or paste an image URL" className={inputCls} />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <Label>One-line Highlight</Label>
        <input data-testid="admin-form-note" value={form.note} onChange={set("note")} placeholder="e.g. Sea-facing tower residences with private decks" className={inputCls} />
      </div>

      <div className="mt-8 flex gap-3">
        <button
          data-testid="admin-form-save"
          disabled={save.isPending}
          className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3 text-sm font-bold text-[#0B0C0E] transition-colors hover:bg-[#F3E5AB] disabled:opacity-60"
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEdit ? "Save Changes" : "Publish Project"}
        </button>
        <button
          type="button"
          data-testid="admin-form-cancel"
          onClick={onClose}
          className="rounded-full border border-white/10 px-7 py-3 text-sm text-[#CBD5E1] transition-colors hover:border-white/30"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
